import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { getTool, getCategories } from '../../graphql/queries';
import { updateTool, deleteTool, getSignedUrl, getObjectUrl } from '../../graphql/mutation';
import {
  Card,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Space,
  Spin,
  Row,
  Col,
  Tag,
  Image,
  Modal,
  InputNumber
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  CloseOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { uploadToR2 } from '../../utils/uploadToR2';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './Studios.css';

const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;

const imageTypes = [
  { value: 'DUAL', label: 'Dual' },
  { value: 'SINGLE', label: 'Single' },
];

const systemProviders = [
  { value: 'WIRO_PX', label: 'Wiro PX' },
  { value: 'WIRO_KLING', label: 'Wiro Kling' },
  { value: 'WIRO_MINIMAX', label: 'Wiro Minimax' },
  { value: 'WIRO_WAN21', label: 'Wiro Wan21' },
  { value: 'WIRO_BYTEDANCE', label: 'Wiro Bytedance' },
  { value: 'FAL', label: 'Fal' },
];

const ToolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState({
    banner: null,
    video: null,
    webm: null,
    webp: null,
  });
  const [previews, setPreviews] = useState({
    banner: null,
    video: null,
    webm: null,
    webp: null,
  });

  const { loading: queryLoading, data, refetch } = useQuery(getTool, {
    variables: { getToolId: id },
    onError: (error) => {
      showSnackBar('error', 'Error', 'Failed to load tool details');
    },
  });

  const { data: categoriesData } = useQuery(getCategories, {
    variables: { pageSize: 100, page: 1 }
  });

  const [updateToolMut] = useMutation(updateTool);
  const [deleteToolMut] = useMutation(deleteTool);
  const [getSignedUrlMut] = useMutation(getSignedUrl);
  const [getObjectUrlMut] = useMutation(getObjectUrl);

  const tool = data?.getTool;

  useEffect(() => {
    if (tool && isEditing) {
      form.setFieldsValue({
        name: tool.name,
        imageType: tool.imageType,
        templateId: tool.templateId,
        prompt: tool.prompt,
        isFeatured: tool.isFeatured,
        duration: tool.duration,
        cost: tool.cost,
        aspectRatios: tool.aspectRatios || [],
        categoryId: tool.categoryId,
        provider: tool.provider,
      });
      setPreviews({
        banner: tool.bannerUrl,
        video: tool.videoUrl,
        webm: tool.webmUrl,
        webp: tool.webpUrl,
      });
    }
  }, [tool, isEditing, form]);

  const handleFileChange = (type, file) => {
    if (file) {
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        showSnackBar('error', 'File Too Large', `${type} must be smaller than 100MB`);
        return false;
      }

      // Validate file types
      if (type === 'banner' && !file.type.startsWith('image/')) {
        showSnackBar('error', 'Invalid File Type', 'Banner must be an image file');
        return false;
      }

      if (type === 'video' && !file.type.startsWith('video/')) {
        showSnackBar('error', 'Invalid File Type', 'Video must be a video file');
        return false;
      }

      if (type === 'webm' && file.type !== 'video/webm') {
        showSnackBar('error', 'Invalid File Type', 'WebM file must be a .webm video file');
        return false;
      }

      if (type === 'webp' && file.type !== 'image/webp') {
        showSnackBar('error', 'Invalid File Type', 'WebP file must be a .webp image file');
        return false;
      }

      setFiles(prev => ({ ...prev, [type]: file }));
      setPreviews(prev => ({ 
        ...prev, 
        [type]: URL.createObjectURL(file) 
      }));
      showSnackBar('success', 'File Selected', `${type} file selected successfully`);
    }
    return false;
  };

  const getSignedUrls = async () => {
    const urls = {};
    for (const [type, file] of Object.entries(files)) {
      if (file) {
        const { data } = await getSignedUrlMut({
          variables: { fileName: file.name }
        });
        urls[type] = data.getSignedUrl;
      }
    }
    return urls;
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFiles({ banner: null, video: null, webm: null, webp: null });
    setPreviews({
      banner: tool?.bannerUrl,
      video: tool?.videoUrl,
      webm: tool?.webmUrl,
      webp: tool?.webpUrl,
    });
    form.resetFields();
  };

  const handleUpdate = async (values) => {
    try {
      setLoading(true);

      let bannerUrl = tool.bannerUrl;
      let videoUrl = tool.videoUrl;
      let webmUrl = tool.webmUrl;
      let webpUrl = tool.webpUrl;

      // Upload new files if selected
      if (Object.values(files).some(file => file)) {
        const signedUrls = await getSignedUrls();
        
        // Upload files
        const uploadPromises = Object.entries(signedUrls).map(([type, urlData]) => 
          uploadToR2(files[type], urlData.url, urlData.contentType)
        );
        
        if (uploadPromises.length > 0) {
          showSnackBar('info', 'Uploading Files', 'Please wait while files are being uploaded...');
          await Promise.all(uploadPromises);

          // Get permanent URLs
          for (const [type, urlData] of Object.entries(signedUrls)) {
            const { data } = await getObjectUrlMut({
              variables: { key: urlData.id, eTag: 'etag' }
            });
            if (type === 'banner') bannerUrl = data.getObjectUrl;
            if (type === 'video') videoUrl = data.getObjectUrl;
            if (type === 'webm') webmUrl = data.getObjectUrl;
            if (type === 'webp') webpUrl = data.getObjectUrl;
          }
        }
      }

      // Filter aspect ratios to only include fields accepted by ToolRatioInput
      const cleanAspectRatios = values.aspectRatios ? values.aspectRatios.map(ratio => ({
        width: ratio.width,
        height: ratio.height,
        name: ratio.name,
        aspectRatioX: ratio.aspectRatioX,
        aspectRatioY: ratio.aspectRatioY,
      })) : [];

      const input = {
        name: values.name,
        imageType: values.imageType,
        bannerUrl,
        videoUrl,
        webmUrl,
        webpUrl,
        templateId: values.templateId && values.templateId.trim() !== '' ? values.templateId.trim() : null,
        prompt: values.prompt || null,
        isFeatured: values.isFeatured,
        duration: values.duration || null,
        cost: values.cost || null,
        aspectRatios: cleanAspectRatios,
        categoryId: values.categoryId || null,
        provider: values.provider,
      };

      await updateToolMut({
        variables: { 
          input,
          updateToolId: id 
        }
      });

      showSnackBar('success', 'Success', 'Tool updated successfully');
      setIsEditing(false);
      setFiles({ banner: null, video: null, webm: null, webp: null });
      refetch();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to update tool');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: 'Delete Tool',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this tool? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteToolMut({
            variables: { deleteToolId: id }
          });
          showSnackBar('success', 'Success', 'Tool deleted successfully');
          navigate('/tools');
        } catch (error) {
          showSnackBar('error', 'Error', 'Failed to delete tool');
        }
      },
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (queryLoading) {
    return (
      <div className="studios-container">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="studios-container">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h3>Tool not found</h3>
            <Button onClick={() => navigate('/tools')}>
              Back to Tools
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="studios-container">
      <Card
        className="studio-detail-card"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Tool Details: {tool.name}</span>
            <Tag color={tool.imageType === 'DUAL' ? 'blue' : 'green'}>
              {tool.imageType}
            </Tag>
            <Tag color={tool.isFeatured ? 'gold' : 'default'}>
              {tool.isFeatured ? 'Featured' : 'Regular'}
            </Tag>
          </div>
        }
        extra={
          <Space>
            <Button onClick={() => navigate('/tools')}>
              Back to List
            </Button>
            {!isEditing ? (
              <>
                <Button 
                  type="primary" 
                  icon={<EditOutlined />} 
                  onClick={handleEdit}
                >
                  Edit
                </Button>
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </>
            ) : (
              <>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  onClick={() => form.submit()}
                  loading={loading}
                >
                  Save
                </Button>
                <Button 
                  icon={<CloseOutlined />} 
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            )}
          </Space>
        }
      >
        {!isEditing ? (
          <div className="studio-details-view">
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              {/* Left side - Tool Information */}
              <div style={{ flex: 1 }}>
                <h3 style={{ marginBottom: '20px', color: '#1f1f1f', fontSize: '18px' }}>Tool Information</h3>
                <Row gutter={[20, 12]}>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>ID</label>
                      <div className="detail-value" style={{ fontWeight: '500' }}>{tool.id}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Type</label>
                      <div className="detail-value">
                        <Tag color={tool.imageType === 'DUAL' ? 'blue' : 'green'} style={{ margin: 0 }}>
                          {tool.imageType}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Status</label>
                      <div className="detail-value">
                        <Tag color={tool.isFeatured ? 'gold' : 'default'} style={{ margin: 0 }}>
                          {tool.isFeatured ? 'Featured' : 'Regular'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Template ID</label>
                      <div className="detail-value">{tool.templateId || 'None'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Duration</label>
                      <div className="detail-value">{tool.duration ? `${tool.duration} seconds` : 'Not set'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Cost</label>
                      <div className="detail-value">{tool.cost ? `$${tool.cost}` : 'Not set'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Provider</label>
                      <div className="detail-value">
                        <Tag color="blue" style={{ margin: 0 }}>
                          {tool.provider ? systemProviders.find(p => p.value === tool.provider)?.label || tool.provider : 'Not set'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Video Count</label>
                      <div className="detail-value" style={{ fontWeight: '500', color: '#1890ff' }}>
                        {tool.videoCount || 0}
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Created</label>
                      <div className="detail-value">{formatDate(tool.createdAt)}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Last Updated</label>
                      <div className="detail-value">{formatDate(tool.updatedAt)}</div>
                    </div>
                  </Col>
                </Row>

                {/* Aspect Ratios Section */}
                {tool.aspectRatios && tool.aspectRatios.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '16px', color: '#1f1f1f', fontSize: '18px' }}>Aspect Ratios</h3>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {tool.aspectRatios.map((ratio, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            background: '#f8f9fa', 
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            border: '1px solid #e9ecef',
                            minWidth: '180px'
                          }}
                        >
                          <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>
                            {ratio.name}
                          </div>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            <div>Size: {ratio.width} × {ratio.height}</div>
                            <div>Ratio: {ratio.aspectRatioX}:{ratio.aspectRatioY}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tool.prompt && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '12px', color: '#1f1f1f', fontSize: '18px' }}>Prompt</h3>
                    <div className="prompt-text" style={{ 
                      background: '#f8f9fa', 
                      padding: '12px', 
                      borderRadius: '6px', 
                      border: '1px solid #e9ecef',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      {tool.prompt}
                    </div>
                  </div>
                )}
              </div>

              {/* Right side - Media */}
              <div style={{ width: '280px', flexShrink: 0 }}>
                <h3 style={{ marginBottom: '16px', color: '#1f1f1f', fontSize: '18px' }}>Media</h3>
                
                <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Banner
                    </label>
                    {tool.bannerUrl ? (
                      <Image
                        src={tool.bannerUrl}
                        alt="Banner"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px'
                      }}>
                        No banner
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Video
                    </label>
                    {tool.videoUrl ? (
                      <video
                        src={tool.videoUrl}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px',
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <PlayCircleOutlined style={{ fontSize: '16px' }} />
                        No video
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebM Video
                    </label>
                    {tool.webmUrl ? (
                      <video
                        src={tool.webmUrl}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px',
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <PlayCircleOutlined style={{ fontSize: '16px' }} />
                        No WebM video
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebP Image
                    </label>
                    {tool.webpUrl ? (
                      <Image
                        src={tool.webpUrl}
                        alt="WebP Image"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px'
                      }}>
                        No WebP image
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdate}
          >
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              {/* Left side - Form Fields */}
              <div style={{ flex: 1 }}>
                <h3 style={{ marginBottom: '20px', color: '#1f1f1f', fontSize: '18px' }}>Edit Tool Information</h3>
                
                <Row gutter={[20, 16]}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="Tool Name"
                      rules={[{ required: true, message: 'Please enter tool name' }]}
                    >
                      <Input placeholder="Enter tool name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="imageType"
                      label="Image Type"
                      rules={[{ required: true, message: 'Please select image type' }]}
                    >
                      <Select placeholder="Select image type">
                        {imageTypes.map(type => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="templateId"
                      label="Template ID"
                    >
                      <Input
                        style={{ width: '100%' }}
                        placeholder="Enter template ID"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="duration"
                      label="Duration (seconds)"
                    >
                      <InputNumber
                        min={1}
                        max={30}
                        style={{ width: '100%' }}
                        placeholder="Enter duration"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="cost"
                      label="Cost ($)"
                    >
                      <InputNumber
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                        placeholder="Enter cost in dollars"
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="isFeatured"
                      label="Featured"
                    >
                      <Select placeholder="Is this tool featured?">
                        <Option value={true}>Yes</Option>
                        <Option value={false}>No</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="categoryId"
                      label="Category"
                    >
                      <Select placeholder="Select category" allowClear>
                        {categoriesData?.getCategories?.map(category => (
                          <Option key={category.id} value={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="provider"
                      label="Provider"
                      rules={[{ required: true, message: 'Please select a provider' }]}
                    >
                      <Select placeholder="Select provider">
                        {systemProviders.map(provider => (
                          <Option key={provider.value} value={provider.value}>
                            {provider.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Aspect Ratios">
                      <Form.List name="aspectRatios">
                        {(fields, { add, remove }) => (
                          <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 16 }}>
                            {fields.map(({ key, name, ...restField }) => (
                              <Row key={key} gutter={8} style={{ marginBottom: 12, alignItems: 'center' }}>
                                <Col span={5}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'name']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <Input placeholder="Name" size="small" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'width']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="W"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                                  ×
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'height']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="H"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'aspectRatioX']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="X"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                                  :
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'aspectRatioY']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="Y"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={1}>
                                  {fields.length > 1 && (
                                    <Button
                                      type="text"
                                      icon={<MinusCircleOutlined />}
                                      onClick={() => remove(name)}
                                      size="small"
                                      danger
                                      style={{ color: '#ff4d4f' }}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                            
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                              size="small"
                              style={{ marginTop: 8 }}
                            >
                              Add Ratio
                            </Button>
                          </div>
                        )}
                      </Form.List>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="prompt"
                  label="Prompt"
                  style={{ marginTop: '16px' }}
                >
                  <TextArea 
                    rows={3} 
                    placeholder="Enter prompt"
                  />
                </Form.Item>
              </div>

              {/* Right side - Media Upload */}
              <div style={{ width: '280px', flexShrink: 0 }}>
                <h3 style={{ marginBottom: '16px', color: '#1f1f1f', fontSize: '18px' }}>Update Media</h3>
                
                <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Banner Image
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('banner', file)}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                      >
                        {files.banner ? 'Change Banner' : 'Upload Banner'}
                      </Button>
                    </Upload>
                    {previews.banner && (
                      <Image
                        src={previews.banner}
                        alt="Banner preview"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Video Preview
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('video', file)}
                      showUploadList={false}
                      accept="video/*"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                      >
                        {files.video ? 'Change Video' : 'Upload Video'}
                      </Button>
                    </Upload>
                    {previews.video && (
                      <video
                        src={previews.video}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebM Video
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('webm', file)}
                      showUploadList={false}
                      accept=".webm,video/webm"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                      >
                        {files.webm ? 'Change WebM' : 'Upload WebM'}
                      </Button>
                    </Upload>
                    {previews.webm && (
                      <video
                        src={previews.webm}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebP Image
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('webp', file)}
                      showUploadList={false}
                      accept=".webp,image/webp"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                      >
                        {files.webp ? 'Change WebP' : 'Upload WebP'}
                      </Button>
                    </Upload>
                    {previews.webp && (
                      <Image
                        src={previews.webp}
                        alt="WebP preview"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default ToolDetail; 